import React, { useCallback } from "react";
import { Image, ResponsiveImageType } from "react-datocms";
import { Text } from "../design-system/components/Text";
import { getTime } from "date-fns";
import { useActiveBannerQuery } from "../graphql/products/hooks/useActiveBannerQuery";
import { TextLink } from "../design-system/components/TextLink";
import styled, { css } from "../styled";
import Icon from "./Icon";
import { ClearIcon } from "@echo-health/icons-web";
import useWindowDimensions from "../hooks/useWindowDimensions";
import useCookie from "../hooks/useCookie";

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: auto 24px;
  grid-template-areas: "content trailing";
  grid-column-gap: ${p => p.theme.spacing.s};

  flex: 1;
  width: 100%;
  background: ${p => p.theme.color.white};
  padding: 12px ${p => p.theme.spacing.m};

  ${({ theme }) => css`
    @media screen and (min-width: ${theme.breakpoints.m}) {
      display: grid;
      grid-template-columns: 24px auto 24px;
      grid-template-areas: "leading content trailing";
      grid-column-gap: ${theme.spacing.s};
    }
  `}
`;

const Body = styled.div`
  grid-area: content;
  flex-grow: 1;

  display: flex;
  flex-direction: row;
  align-items: flex-start;

  ${({ theme }) => css`
    @media screen and (min-width: ${theme.breakpoints.m}) {
      justify-self: center;
      align-items: center;
    }
  `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => css`
    @media screen and (min-width: ${theme.breakpoints.m}) {
      > p {
        text-align: center;
      }
    }
  `}
`;

const DismissButtonWrapper = styled.div`
  grid-area: trailing;
  flex-shrink: 0;
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-decoration: none;

  padding: ${p => p.theme.spacing.xs};
  margin: -${p => p.theme.spacing.xs};

  color: ${p => p.theme.color.gray40};

  border-radius: 50%;
  appearance: none;
  cursor: pointer;
  transition: background 200ms ease-in-out, box-shadow 200ms ease-in-out;

  &:hover,
  &:focus,
  &:active {
    color: ${p => p.theme.color.blue60};
  }

  &:hover {
    background: ${p => p.theme.color.blue10};
  }

  &:focus:not(:hover) {
    box-shadow: ${p => `${p.theme.shadow.focusRing} ${p.theme.color.blue20}`};
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-right: ${p => p.theme.spacing.s};

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const AnnouncementBanner = () => {
  const dimensions = useWindowDimensions();
  const { data } = useActiveBannerQuery({});

  const activeBannerId =
    data &&
    data.activeBanner &&
    data.activeBanner.web &&
    data.activeBanner.web.id;

  const selectCookieValue = useCallback(value => {
    const dismissedAt = Number(value);

    return { isDismissed: !!dismissedAt };
  }, []);

  const [cookie, setCookie] = useCookie(`${activeBannerId}-banner`, {
    persistLocalStorage: true,
    select: selectCookieValue,
    initialValue: "0",
    skip: !activeBannerId
  });

  const isDismissed = !!cookie && cookie.isDismissed;

  if (
    !data ||
    !data.activeBanner ||
    !data.activeBanner.web ||
    !cookie ||
    isDismissed
  ) {
    return null;
  }

  const bannerImage =
    data.activeBanner.web.image && data.activeBanner.web.image.responsiveImage;

  const width = dimensions && dimensions.width;

  return (
    <Wrapper>
      <Body>
        {bannerImage && (
          <ImageWrapper>
            <Image
              data={
                {
                  ...bannerImage,
                  alt: bannerImage.alt || ""
                } as ResponsiveImageType
              }
            />
          </ImageWrapper>
        )}
        <Content>
          <Text isBold color="gray70">
            {data.activeBanner.web.text}
            {width &&
              width >= 800 &&
              data.activeBanner.web.linkLabel &&
              data.activeBanner.web.linkUrl && (
                <>
                  {
                    " " /* Add a cheeky trailing space to separate the link from the main text */
                  }
                  <TextLink
                    label={data.activeBanner.web.linkLabel}
                    linkTo={data.activeBanner.web.linkUrl}
                  />
                </>
              )}
          </Text>
          {width && width < 800 && (
            /**
             * On desktop our link needs to be an inline element within the text content,
             * we _could_ just have them as separate block elements with flex-direction: row,
             * but at the point where the text is long enough to wrap it would 'squeeze' the link
             * instead of the link wrapping with the text
             *
             * On *mobile* we need to have the text and link as separate stacked block elements
             * with flex-direction: column, as such, there's no good way to do this with just media
             * queries and we actually have to alter the markup at different breakpoints instead,
             * hence utilising `useWindowDimensions`
             */
            <>
              {data.activeBanner.web.linkLabel &&
                data.activeBanner.web.linkUrl && (
                  <TextLink
                    margin="xs 0 0"
                    label={data.activeBanner.web.linkLabel}
                    linkTo={data.activeBanner.web.linkUrl}
                  />
                )}
            </>
          )}
        </Content>
      </Body>
      {data.activeBanner.web.dismissable && (
        <DismissButtonWrapper>
          <DismissButton
            onClick={() =>
              setCookie(`${activeBannerId}-banner`, String(getTime(new Date())))
            }
            aria-label="dismiss"
          >
            <Icon type={ClearIcon} color="currentColor" />
          </DismissButton>
        </DismissButtonWrapper>
      )}
    </Wrapper>
  );
};

export default AnnouncementBanner;
